import { ThemedText } from '@/components/themed-text';
import React from 'react';
import { Linking, Pressable, View } from 'react-native';

// TODO(T8.2): revisão jurídica antes de ir ao ar

/**
 * Versão do texto do recebimento automático (seção de Termos e o parágrafo
 * de Privacidade abaixo). Quando o aceite passar a ser gravado (T1.6), este
 * valor precisa ser idêntico à chave `payments.terms_version` em
 * `platform_settings` — é o que compara o aceite salvo do prestador com a
 * versão vigente do texto. Atualize os dois juntos sempre que o texto mudar.
 */
export const PAYMENTS_TERMS_VERSION = '2026-09-22';

const STRIPE_CONNECTED_ACCOUNT_AGREEMENT_URL = 'https://stripe.com/br/legal/connect-account';
const STRIPE_PRIVACY_POLICY_URL = 'https://stripe.com/br/privacy';

function ExternalLinkLine({ href, label }: { href: string; label: string }) {
  return (
    <Pressable onPress={() => void Linking.openURL(href)} className="py-0.5">
      <ThemedText className="font-medium text-base text-primary underline">{label}</ThemedText>
    </Pressable>
  );
}

/**
 * Seção "Recebimento pela Controlapag (Stripe Connect)" dos Termos de Uso.
 * Usada em `app/terms.tsx`, um só
 * lugar para o texto e a versão não desalinharem entre as duas telas.
 */
export function PaymentsTermsSection({ sectionNumber }: { sectionNumber: number }) {
  return (
    <View className="mb-8 gap-2">
      <ThemedText role="heading" aria-level={2} className="font-semibold text-xl text-primary">
        {sectionNumber}. Recebimento pelo link (Stripe Connect)
      </ThemedText>
      <ThemedText className="text-justify text-base leading-relaxed opacity-80">
        Quando você ativa o recebimento automático, a Controlapag continua sendo uma plataforma de
        gestão: você é o vendedor perante o seu cliente, e quem presta o serviço, emite a cobrança e
        responde por ela é você. O processamento do pagamento (cartão, Pix e boleto) é feito pela
        Stripe, por meio de uma conta conectada aberta em seu nome (Stripe Connect).
        {/*
          D4 (seção 10 do plano) ainda não fechada: a recomendação é oferecer pessoa física e
          CNPJ no mesmo assistente de ativação, com pessoa física como padrão. A frase abaixo
          já cobre os dois; se a decisão limitar a um, ajustar o texto.
        */}{' '}
        Essa conta pode estar no seu CPF ou no CNPJ do seu negócio, conforme o que você informar no
        cadastro. Ao concluir o cadastro no componente da própria Stripe, dentro do app, você aceita
        diretamente o Contrato de Conta Conectada da Stripe e a Política de Privacidade da Stripe,
        que regem o processamento do pagamento e o tratamento de dados feito pela Stripe. A
        Controlapag não é parte nesse contrato:
      </ThemedText>
      <View className="gap-1 pl-1">
        <ExternalLinkLine
          href={STRIPE_CONNECTED_ACCOUNT_AGREEMENT_URL}
          label="Contrato de Conta Conectada da Stripe"
        />
        <ExternalLinkLine
          href={STRIPE_PRIVACY_POLICY_URL}
          label="Política de Privacidade da Stripe"
        />
      </View>
      <ThemedText className="text-justify text-base leading-relaxed opacity-80">
        As bandeiras de cartão aceitas, os limites de valor por forma de pagamento e as taxas
        cobradas em cada pagamento são sempre os vigentes na tela Configurar Recebimento, dentro do
        app. Elas também estão em controlapag.com.br/ajuda/recebimentos.
        {/*
          D1 (seção 10 do plano) ainda não fechada: a recomendação é margem 0% da Controlapag no
          lançamento, configurável em `platform_settings`. A frase abaixo fica genérica de
          propósito; se D1 mudar para uma margem própria, revisar este parágrafo (e avisar quem
          já ativou) antes de qualquer cobrança adicional entrar em vigor.
        */}{' '}
        Além das taxas da própria Stripe, mostradas nessa tela, a Controlapag só cobra uma margem
        própria sobre o recebimento se isso estiver explícito ali antes de você ativar.
      </ThemedText>
      <ThemedText className="text-justify text-base leading-relaxed opacity-80">
        CPF, data de nascimento e dados bancários informados no cadastro do recebimento automático
        passam pelos nossos servidores apenas para serem enviados à Stripe, uma única vez, e não são
        guardados nem registrados em log por nós: exibimos apenas o CPF mascarado e os últimos
        dígitos da conta, lidos da Stripe. O aceite desta seção (data, IP e a versão do texto
        abaixo) fica registrado por nós.
      </ThemedText>
      <ThemedText className="text-justify text-base leading-relaxed opacity-80">
        Na ativação, dentro do componente da própria Stripe, ela pode pedir a confirmação de um
        código enviado por SMS ou e-mail, para confirmar que é você.
      </ThemedText>
      <ThemedText className="text-xs opacity-50">
        Versão deste texto: {PAYMENTS_TERMS_VERSION}
      </ThemedText>
    </View>
  );
}

/**
 * Parágrafo curto sobre o recebimento automático na Política de Privacidade
 * (seção 4.8 do plano): o que vai para a Stripe, o que a Controlapag guarda
 * e o link para a política de privacidade da Stripe. Mesma constante de
 * versão da seção de Termos acima — os dois textos mudam juntos.
 */
export function PaymentsPrivacySection({ sectionNumber }: { sectionNumber: number }) {
  return (
    <View className="mb-8 gap-2">
      <ThemedText role="heading" aria-level={2} className="font-semibold text-xl text-primary">
        {sectionNumber}. Recebimento pelo link (Stripe Connect)
      </ThemedText>
      <ThemedText className="text-justify text-base leading-relaxed opacity-80">
        Se você ativa o recebimento automático, o CPF, a data de nascimento e os dados bancários que
        você informa no cadastro passam pelos nossos servidores só para serem enviados à Stripe, uma
        única vez, e não são guardados nem registrados em log por nós. É a Stripe quem processa o
        pagamento e cuida da verificação de identidade exigida por lei. Guardamos só o necessário
        para mostrar na tela: CPF mascarado (ex.: ***.456.789-**), banco e os últimos 4 dígitos da
        conta, lidos da própria Stripe. O aceite dos termos de recebimento fica registrado por nós,
        com data, IP e a versão do texto ({PAYMENTS_TERMS_VERSION}).
      </ThemedText>
      <View className="gap-1 pl-1">
        <ExternalLinkLine
          href={STRIPE_PRIVACY_POLICY_URL}
          label="Política de Privacidade da Stripe"
        />
      </View>
    </View>
  );
}
